import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  Subject,
  Subscription,
  forkJoin,
  of,
  shareReplay,
  TimeoutError,
  throwError,
  timer,
} from 'rxjs';
import { catchError, map, switchMap, tap, timeout } from 'rxjs/operators';
import { ServiceConstant } from '../utils/service-constant.util';
import { FNFResponse } from '../models/response';
import { RestaurantService } from './restaurant.service';
import {
  AvailabilitySlot,
  CreateReservationInput,
  GuestLookup,
  JoinWaitlistInput,
  Reservation,
  ReservationMode,
  ReservationSettings,
  ReservationStatus,
  ReservationTable,
  ServicePeriod,
  ServingHoursState,
  StandingNotesChange,
} from '../models/reservation';
import {
  fromCreate,
  fromGuest,
  fromSettings,
  fromStandingNotes,
  fromWaitlistJoin,
  toGuestLookup,
  toReservation,
  toServicePeriods,
  toServingHoursState,
  toSettings,
  toSlot,
  toTable,
} from './reservation-api.mapper';
import { DEFAULT_RESTAURANT_TIME_ZONE } from './reservation-time.util';
import { ServingHoursDoc, exceptionOn } from '../utils/serving-hours.util';

/**
 * Reservation API for the dashboard — `/v1/restaurants/reservations/*` on the
 * gateway (reservation-service behind it) plus the `reservation` settings row
 * on restaurant-service. The restaurant is taken from the staff JWT, so no
 * call carries an id.
 *
 * `changes$` fires after every successful write so open views refetch.
 * Errors surface as `Error` with the API's message, never swallowed.
 */
@Injectable({ providedIn: 'root' })
export class ReservationService {
  private readonly http = inject(HttpClient);
  private readonly base = ServiceConstant.apiUrl + '/restaurants';
  private readonly reservations = this.base + '/reservations';

  private readonly changes = new Subject<void>();
  readonly changes$ = this.changes.asObservable();

  /** The current restaurant's reservation mode from the last settings read or
   * save; null until one has happened, and reset when the restaurant changes.
   * The sidebar and ReservationGuard gate on it. */
  readonly mode$ = new BehaviorSubject<ReservationMode | null>(null);

  /**
   * Every pending request across all dates that has not expired, soonest
   * deadline first (FNF-308) — the Requests queue on the book. Kept fresh by
   * the request poll, after every write here, and on a RESERVATION_REQUESTED
   * push (`refreshRequests()`).
   */
  readonly pendingRequests$ = new BehaviorSubject<Reservation[]>([]);

  /** How many requests are waiting: the badge on the sidebar's Reservations entry. */
  readonly pendingRequestCount$ = new BehaviorSubject<number>(0);

  /**
   * How often the request count is re-read while the dashboard is open. Push
   * makes it live where the browser allows; the poll is the floor, as for
   * the header bell (`NotificationService.pollMs`).
   */
  static readonly requestPollMs = 60_000;

  /**
   * How long a read may take before the book gives up on it (FNF-444). The
   * gateway answers 504 well before this; it only bounds a stalled network,
   * so a host never waits on a spinner mid-service. Writes have no ceiling:
   * a write the browser abandons may still land.
   */
  static readonly readTimeoutMs = 20_000;

  private requestPoll?: Subscription;

  /** Restaurant details are fetched once per page load for service hours. */
  private restaurant$?: Observable<Record<string, unknown>>;

  /** Whose mode `mode$` holds, so a restaurant switch can drop it. */
  private modeRestaurantId: string | null = null;

  constructor() {
    // Saving serving hours on Restaurant Profile republishes the restaurant, so
    // drop the cached copy and the reservation views pick up the new hours.
    inject(RestaurantService).currentRestaurantDetails$.subscribe((restaurant: any) => {
      this.restaurant$ = undefined;
      // Select Restaurant switches restaurants without a reload, so the previous
      // restaurant's mode must not carry over to the next one. The same
      // restaurant being republished (e.g. a profile save) keeps it.
      const restaurantId: string | null = restaurant?.restaurant_id ?? null;
      if (restaurantId && restaurantId !== this.modeRestaurantId) {
        if (this.modeRestaurantId !== null) {
          this.mode$.next(null);
          // Nor may its requests: the badge would count another restaurant's.
          this.publishRequests([]);
        }
        this.modeRestaurantId = restaurantId;
      }
    });
  }

  // ------------------------------------------------------------ reads

  /** GET /v1/restaurants/reservations?date= */
  listByDate(serviceDate: string): Observable<Reservation[]> {
    return this.read<Record<string, unknown>[]>(this.reservations, { date: serviceDate }).pipe(
      map((rows) => (rows ?? []).map(toReservation)),
    );
  }

  /** GET /v1/restaurants/reservations/waitlist?date= */
  waitlist(serviceDate: string): Observable<Reservation[]> {
    return this.read<Record<string, unknown>[]>(this.reservations + '/waitlist', {
      date: serviceDate,
    }).pipe(map((rows) => (rows ?? []).map(toReservation)));
  }

  /**
   * GET …/restaurants/reservations/requests (FNF-308): every pending request
   * not yet expired, across all dates, soonest deadline first.
   */
  listRequests(): Observable<Reservation[]> {
    return this.read<Record<string, unknown>[]>(this.reservations + '/requests').pipe(
      map((rows) => (rows ?? []).map(toReservation)),
    );
  }

  /** `listRequests()`, publishing the result to the queue and the badge. */
  loadRequests(): Observable<Reservation[]> {
    return this.listRequests().pipe(tap((rows) => this.publishRequests(rows)));
  }

  /** Re-reads the queue without a caller having to care about the result. */
  refreshRequests(): void {
    this.loadRequests().subscribe({ error: () => undefined });
  }

  /**
   * Starts the request poll. Idempotent, and quiet while the tab is hidden.
   * The sidebar runs it only while reservations are rolled out and the mode
   * can produce requests (`REQUEST_MODES`).
   */
  startRequestPolling(): void {
    if (this.requestPoll) return;
    this.requestPoll = timer(0, ReservationService.requestPollMs)
      .pipe(
        switchMap(() =>
          typeof document !== 'undefined' && document.visibilityState === 'hidden'
            ? EMPTY
            : // One failed tick must not end the poll.
              this.loadRequests().pipe(catchError(() => EMPTY)),
        ),
      )
      .subscribe({ error: () => undefined });
  }

  /**
   * Stops the poll and clears the badge (mode off, flag off, sign-out). A
   * no-op when no poll runs: the sidebar calls it on every menu change.
   */
  stopRequestPolling(): void {
    if (!this.requestPoll) return;
    this.requestPoll.unsubscribe();
    this.requestPoll = undefined;
    this.publishRequests([]);
  }

  /** Whether the request poll is running. */
  get pollingRequests(): boolean {
    return !!this.requestPoll;
  }

  /**
   * A booking changed somewhere else, e.g. a RESERVATION_REQUESTED push
   * (FNF-308): open views refetch as after a write here. The request queue
   * is not re-read; call `refreshRequests()` for that.
   */
  notifyChanged(): void {
    this.changes.next();
  }

  /** GET /v1/restaurants/reservations/{id} */
  get(id: string): Observable<Reservation | undefined> {
    return this.read<Record<string, unknown>>(`${this.reservations}/${id}`).pipe(
      map((row) => (row ? toReservation(row) : undefined)),
    );
  }

  /**
   * GET /v1/restaurants/reservations/availability?date=&party_size=
   * Staff availability: the engine skips the online-notice window for hosts.
   */
  availability(serviceDate: string, partySize: number): Observable<AvailabilitySlot[]> {
    return forkJoin({
      body: this.read<{ slots?: Record<string, unknown>[] }>(this.reservations + '/availability', {
        date: serviceDate,
        party_size: String(partySize),
      }),
      timeZone: this.timeZone(),
    }).pipe(map(({ body, timeZone }) => (body?.slots ?? []).map((s) => toSlot(s, timeZone))));
  }

  /** Seatable tables, from `/v1/restaurants/tables`. */
  tables(): Observable<ReservationTable[]> {
    return this.read<Record<string, unknown>[]>(this.base + '/tables').pipe(
      map((rows) => (rows ?? []).map(toTable).filter((t) => t.id && t.seats > 0)),
    );
  }

  private restaurantDetails(): Observable<Record<string, unknown>> {
    if (!this.restaurant$) {
      this.restaurant$ = this.read<Record<string, unknown>>(this.base).pipe(
        map((r) => r ?? {}),
        shareReplay(1),
      );
    }
    return this.restaurant$;
  }

  /**
   * The restaurant's IANA time zone. Slots, service hours and bookings are on
   * its clock, so the book shows them that way wherever the viewer is. Falls
   * back the way reservation-service does when the restaurant has none.
   */
  timeZone(): Observable<string> {
    return this.restaurantDetails().pipe(
      map((r) => (r['time_zone'] as string) || DEFAULT_RESTAURANT_TIME_ZONE),
      catchError(() => of(DEFAULT_RESTAURANT_TIME_ZONE)),
    );
  }

  /** Service periods for a date, from the restaurant's serving hours. */
  servicePeriods(serviceDate: string): Observable<ServicePeriod[]> {
    return this.restaurantDetails().pipe(
      map((r) => toServicePeriods(r['serving_hours'] as Record<string, unknown>, serviceDate)),
      catchError(() => of([] as ServicePeriod[])),
    );
  }

  /**
   * Whether the restaurant serves on a date. Tells "no hours set up" and
   * "closed that day" apart from a fully booked day, since all three come
   * back from availability as no slots. Falls back to `open` when the
   * restaurant can't be read, so no misleading setup prompt is shown.
   */
  servingHoursState(serviceDate: string): Observable<ServingHoursState> {
    return this.restaurantDetails().pipe(
      map((r) => toServingHoursState(r['serving_hours'] as Record<string, unknown>, serviceDate)),
      catchError(() => of<ServingHoursState>('open')),
    );
  }

  /** The restaurant's note for a closed date or special hours, e.g. "Christmas". */
  dayNote(serviceDate: string): Observable<string | null> {
    return this.restaurantDetails().pipe(
      map(
        (r) =>
          exceptionOn(r['serving_hours'] as ServingHoursDoc | null, serviceDate)?.note?.trim() ||
          null,
      ),
      catchError(() => of(null)),
    );
  }

  /** Everything the book needs for one date, in one round trip of requests. */
  loadDay(serviceDate: string): Observable<{
    settings: ReservationSettings;
    reservations: Reservation[];
    waitlist: Reservation[];
    tables: ReservationTable[];
    periods: ServicePeriod[];
    hoursState: ServingHoursState;
    dayNote: string | null;
    timeZone: string;
  }> {
    return forkJoin({
      settings: this.getSettings(),
      reservations: this.listByDate(serviceDate),
      waitlist: this.waitlist(serviceDate),
      tables: this.tables(),
      periods: this.servicePeriods(serviceDate),
      hoursState: this.servingHoursState(serviceDate),
      dayNote: this.dayNote(serviceDate),
      timeZone: this.timeZone(),
    });
  }

  // ----------------------------------------------------------- writes

  /** POST /v1/restaurants/reservations */
  create(input: CreateReservationInput): Observable<Reservation> {
    return this.write<Record<string, unknown>>('POST', this.reservations, fromCreate(input)).pipe(
      map(toReservation),
    );
  }

  /** PATCH /v1/restaurants/reservations/{id} */
  update(
    id: string,
    patch: Partial<
      Pick<Reservation, 'startAt' | 'partySize' | 'guest' | 'notes' | 'occasion' | 'tableIds'>
    > & { version?: number; standingNotes?: StandingNotesChange | null },
  ): Observable<Reservation> {
    const body: Record<string, unknown> = {};
    if (patch.standingNotes) body['standing_notes'] = fromStandingNotes(patch.standingNotes);
    if (patch.startAt !== undefined) body['start_at'] = patch.startAt;
    if (patch.partySize !== undefined) body['party_size'] = patch.partySize;
    if (patch.guest !== undefined) body['guest'] = fromGuest(patch.guest);
    if (patch.notes !== undefined) body['notes'] = patch.notes;
    if (patch.occasion !== undefined) body['occasion'] = patch.occasion;
    if (patch.tableIds !== undefined) body['table_ids'] = patch.tableIds;
    if (patch.version !== undefined) body['version'] = patch.version;
    return this.write<Record<string, unknown>>('PATCH', `${this.reservations}/${id}`, body).pipe(
      map(toReservation),
    );
  }

  /**
   * PATCH /v1/restaurants/reservations/{id} with only `standing_notes`: the
   * guest's notes, so it works on a seated or finished booking too and leaves
   * the booking (and its version) as it is.
   */
  saveStandingNotes(id: string, change: StandingNotesChange): Observable<Reservation> {
    return this.update(id, { standingNotes: change });
  }

  /**
   * GET …/reservations/guests/lookup: a returning guest's history and
   * standing notes, before a booking is made (FNF-442). Null when the server
   * has no such route (prod's /v1 gateway, an older reservation-service) or
   * the lookup fails: New reservation then just works without the greeting.
   */
  lookupGuest(query: {
    phone?: string | null;
    email?: string | null;
    userId?: string | null;
  }): Observable<GuestLookup | null> {
    const params: Record<string, string> = {};
    if (query.phone) params['phone'] = query.phone;
    if (query.email) params['email'] = query.email;
    if (query.userId) params['user_id'] = query.userId;
    return this.read<Record<string, unknown>>(this.reservations + '/guests/lookup', params).pipe(
      map(toGuestLookup),
      catchError(() => of(null)),
    );
  }

  /**
   * PATCH /v1/restaurants/reservations/{id}/status. `reason` goes with a
   * decline (FNF-308, up to 200 characters) and is passed on to the guest.
   */
  transition(id: string, status: ReservationStatus, reason?: string): Observable<Reservation> {
    return this.write<Record<string, unknown>>('PATCH', `${this.reservations}/${id}/status`, {
      status,
      ...(reason ? { reason } : {}),
    }).pipe(map(toReservation));
  }

  /** PATCH /v1/restaurants/reservations/{id}/status { status: seated, table_ids } */
  seat(id: string, tableIds: string[], early = false): Observable<Reservation> {
    return this.write<Record<string, unknown>>('PATCH', `${this.reservations}/${id}/status`, {
      status: 'seated',
      table_ids: tableIds,
      // Today's booking, guest here before the seat window: it moves to now.
      ...(early ? { early: true } : {}),
    }).pipe(map(toReservation));
  }

  /** POST /v1/restaurants/reservations/waitlist */
  joinWaitlist(input: JoinWaitlistInput): Observable<Reservation> {
    return this.write<Record<string, unknown>>(
      'POST',
      this.reservations + '/waitlist',
      fromWaitlistJoin(input),
    ).pipe(map(toReservation));
  }

  // ---------------------------------------------------------- settings

  /** GET /v1/restaurants/settings → the `reservation` row (defaults when absent). */
  getSettings(): Observable<ReservationSettings> {
    return this.read<{ setting_type: string; settings_config: Record<string, unknown> }[]>(
      this.base + '/settings',
    ).pipe(
      map((rows) =>
        toSettings((rows ?? []).find((r) => r.setting_type === 'reservation')?.settings_config),
      ),
      tap((settings) => this.mode$.next(settings.mode)),
    );
  }

  /** PUT /v1/restaurants/settings/reservation (409 when `version` is stale). */
  saveSettings(settings: ReservationSettings): Observable<ReservationSettings> {
    return this.write<{ settings_config: Record<string, unknown> }>(
      'PUT',
      this.base + '/settings/reservation',
      fromSettings(settings),
    ).pipe(
      map((row) => toSettings(row?.settings_config)),
      tap((saved) => this.mode$.next(saved.mode)),
    );
  }

  /** The current mode, fetching the settings when none has been read yet. */
  loadMode(): Observable<ReservationMode> {
    const known = this.mode$.value;
    return known ? of(known) : this.getSettings().pipe(map((settings) => settings.mode));
  }

  // ------------------------------------------------------------ plumbing

  private publishRequests(rows: Reservation[]): void {
    this.pendingRequests$.next(rows);
    this.pendingRequestCount$.next(rows.length);
  }

  private read<T>(url: string, params?: Record<string, string>): Observable<T> {
    return this.http
      .get<FNFResponse<T>>(url, { params: new HttpParams({ fromObject: params ?? {} }) })
      .pipe(
        timeout(ReservationService.readTimeoutMs),
        map((res) => res.data as T),
        catchError((err) => throwError(() => toError(err))),
      );
  }

  private write<T>(method: 'POST' | 'PATCH' | 'PUT', url: string, body: unknown): Observable<T> {
    return this.http.request<FNFResponse<T>>(method, url, { body }).pipe(
      map((res) => res.data as T),
      tap(() => this.wrote()),
      catchError((err) => throwError(() => toError(err))),
    );
  }

  /**
   * After a write: open views refetch, and so does the request queue while
   * anything shows it (the poll's badge, an open Requests view), since an
   * accept, a decline or a new booking changes it (FNF-308).
   */
  private wrote(): void {
    this.changes.next();
    if (this.requestPoll || this.pendingRequests$.observed) {
      this.refreshRequests();
    }
  }
}

/**
 * Statuses that mean the reservation API could not be reached or did not
 * answer (FNF-444), as opposed to answering with an error: the browser got
 * nothing (0), the gateway could not reach or hear back from the service
 * (502, 504), or the service is up but cannot read the restaurant (503).
 */
const UNAVAILABLE_STATUSES = new Set([0, 502, 503, 504]);

const UNAVAILABLE_MESSAGE =
  'The reservation system is not responding right now. Please try again in a moment.';

/**
 * Whether an error from this service means the reservation API is down or
 * unreachable, rather than that it refused the request. The book shows an
 * outage banner for these instead of an empty day.
 */
export function isUnavailable(err: unknown): boolean {
  const status = (err as { status?: number } | null)?.status;
  return status !== undefined && UNAVAILABLE_STATUSES.has(status);
}

/** The gateway wraps errors as `{ error: { message } }`; surface that text. */
function toError(err: unknown): Error {
  if (err instanceof TimeoutError) {
    return withStatus(new Error(UNAVAILABLE_MESSAGE), 0);
  }
  if (err instanceof Error && !(err instanceof HttpErrorResponse)) {
    return err;
  }
  const http = err as HttpErrorResponse;
  // Kong's own 502/504 text ("An invalid response was received from the
  // upstream server") means nothing to a host; say what happened instead.
  if (UNAVAILABLE_STATUSES.has(http?.status)) {
    return withStatus(new Error(UNAVAILABLE_MESSAGE), http.status);
  }
  const body = http?.error as { error?: { message?: string }; message?: string; detail?: unknown };
  const message =
    body?.error?.message ??
    (typeof body?.detail === 'string' ? body.detail : undefined) ??
    (http?.status === 409
      ? 'That changed while you were working. Refresh and try again.'
      : 'Unable to complete your action. Please try again later.');
  return withStatus(new Error(message), http?.status);
}

function withStatus(error: Error, status: number | undefined): Error {
  (error as Error & { status?: number }).status = status;
  return error;
}
